import { useEffect } from "react";
import { StoreButtons } from "./components/StoreButtons";
import { getLegalDocument } from "./content/legal";
import type { LegalDocument } from "./content/legal.types";
import { UI_COPY, type UiCopy } from "./content/translations";
import {
  alternateLocale,
  ORGANIZATION,
  parseRoute,
  pathFor,
  SITE_URL,
  type AppKind,
  type Locale,
  type Route,
} from "./site";

function languagePath(route: Route): string {
  const nextLocale = alternateLocale(route.locale);
  return route.page === "legal"
    ? pathFor(nextLocale, route.app, route.legal)
    : pathFor(nextLocale);
}

function usePageMetadata(route: Route, title: string, description: string) {
  useEffect(() => {
    document.documentElement.lang = route.locale;
    document.title = title;

    const descriptionElement = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (descriptionElement) descriptionElement.content = description;

    const canonicalPath = route.page === "home" ? pathFor(route.locale) : window.location.pathname;
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = `${SITE_URL}${canonicalPath}`;

    const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = title;

    const ogDescription = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
    if (ogDescription) ogDescription.content = description;

    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = `${SITE_URL}${canonicalPath}`;

    if (window.location.hash) {
      requestAnimationFrame(() => {
        document.querySelector(window.location.hash)?.scrollIntoView();
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [description, route, title]);
}

interface HeaderProps {
  route: Route;
  copy: UiCopy;
}

function Header({ route, copy }: HeaderProps) {
  return (
    <>
      <a className="skip-link" href="#main-content">
        {copy.skipToContent}
      </a>
      <header className="site-header">
        <div className="shell header-inner">
          <a className="brand-link" href={pathFor(route.locale)} aria-label={`${ORGANIZATION.tradeName} ${copy.home}`}>
            <img src="/assets/branding/yegna-wordmark.png" alt="YegnaTaxi" width="184" height="66" />
          </a>
          <nav className="main-nav" aria-label={copy.navigationLabel}>
            <a href={`${pathFor(route.locale)}#download`}>{copy.getTheApps}</a>
            <a href={`${pathFor(route.locale)}#legal`}>{copy.legalDocuments}</a>
            <a className="language-link" href={languagePath(route)} hrefLang={alternateLocale(route.locale)}>
              <span aria-hidden="true">文</span>
              {copy.alternateLanguageName}
            </a>
          </nav>
        </div>
      </header>
    </>
  );
}

function AppPreview() {
  return (
    <div className="app-preview" aria-hidden="true">
      <div className="route-line route-line--one" />
      <div className="route-line route-line--two" />
      <div className="phone phone--rider">
        <span className="phone__speaker" />
        <img src="/assets/branding/rider-app-icon.png" alt="" width="180" height="180" />
        <span className="phone__label">Rider</span>
      </div>
      <div className="phone phone--driver">
        <span className="phone__speaker" />
        <img src="/assets/branding/driver-app-icon.png" alt="" width="180" height="180" />
        <span className="phone__label">Driver</span>
      </div>
      <span className="map-dot map-dot--one" />
      <span className="map-dot map-dot--two" />
    </div>
  );
}

interface AppCardProps {
  app: AppKind;
  copy: UiCopy;
}

function AppCard({ app, copy }: AppCardProps) {
  const isRider = app === "rider";
  return (
    <article className={`app-card app-card--${app}`}>
      <div className="app-card__top">
        <img
          src={`/assets/branding/${app}-app-icon.png`}
          alt=""
          width="116"
          height="116"
          loading="lazy"
        />
        <span className="app-card__number">{isRider ? "01" : "02"}</span>
      </div>
      <h3>{isRider ? copy.riderTitle : copy.driverTitle}</h3>
      <p>{isRider ? copy.riderBody : copy.driverBody}</p>
      <StoreButtons app={app} copy={copy} />
      <p className="availability-note">{copy.availableSoon}</p>
    </article>
  );
}

function LegalLinkGroup({ app, locale, copy }: { app: AppKind; locale: Locale; copy: UiCopy }) {
  return (
    <article className="legal-card">
      <span className="legal-card__app">{app === "rider" ? copy.riderDocuments : copy.driverDocuments}</span>
      <a href={pathFor(locale, app, "privacy")}>
        {copy.privacyPolicy}
        <span aria-hidden="true">↗</span>
      </a>
      <a href={pathFor(locale, app, "terms")}>
        {copy.terms}
        <span aria-hidden="true">↗</span>
      </a>
    </article>
  );
}

function HomePage({ route, copy }: { route: Route & { page: "home" }; copy: UiCopy }) {
  usePageMetadata(
    route,
    route.locale === "am" ? "YegnaTaxi — ጉዞዎ፣ ከተማዎ" : "YegnaTaxi — Your ride. Your city.",
    route.locale === "am"
      ? "በኢትዮጵያ ለጉዞና ለመላኪያ Yegna Riderን፣ ለማሽከርከርና ገቢ ለማግኘት Yegna Driverን ያውርዱ።"
      : "Download Yegna Rider for rides and deliveries, or Yegna Driver to drive and earn in Ethiopia.",
  );

  return (
    <>
      <Header route={route} copy={copy} />
      <main id="main-content">
        <section className="hero">
          <div className="shell hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">{copy.heroEyebrow}</p>
              <h1>{copy.heroTitle}</h1>
              <p className="hero-body">{copy.heroBody}</p>
              <div className="hero-actions">
                <a className="button button--primary" href="#download">
                  {copy.heroAction}
                  <span aria-hidden="true">↓</span>
                </a>
                <a className="button button--text" href="#legal">
                  {copy.heroSecondaryAction}
                </a>
              </div>
            </div>
            <AppPreview />
          </div>
          <div className="shell trust-strip" aria-label="Platform highlights">
            <span><i aria-hidden="true">✓</i>{copy.trustEthiopia}</span>
            <span><i aria-hidden="true">Aa</i>{copy.trustLanguages}</span>
            <span><i aria-hidden="true">⌁</i>{copy.trustBandwidth}</span>
          </div>
        </section>

        <section className="apps-section" id="download">
          <div className="shell">
            <div className="section-heading">
              <p className="eyebrow">{copy.appsEyebrow}</p>
              <h2>{copy.appsTitle}</h2>
              <p>{copy.appsBody}</p>
            </div>
            <div className="app-grid">
              <AppCard app="rider" copy={copy} />
              <AppCard app="driver" copy={copy} />
            </div>
          </div>
        </section>

        <section className="legal-section" id="legal">
          <div className="shell legal-grid">
            <div className="section-heading section-heading--left">
              <p className="eyebrow">{copy.legalEyebrow}</p>
              <h2>{copy.legalTitle}</h2>
              <p>{copy.providerLine}</p>
            </div>
            <div className="legal-links-grid">
              <LegalLinkGroup app="rider" locale={route.locale} copy={copy} />
              <LegalLinkGroup app="driver" locale={route.locale} copy={copy} />
            </div>
          </div>
        </section>
      </main>
      <Footer locale={route.locale} copy={copy} />
    </>
  );
}

function LegalPage({ route, copy }: { route: Route & { page: "legal" }; copy: UiCopy }) {
  const document = getLegalDocument(route.locale, route.app, route.legal);
  usePageMetadata(route, `${document.title} | YegnaTaxi`, document.summary);

  return (
    <>
      <Header route={route} copy={copy} />
      <main className="legal-page" id="main-content">
        <div className="shell legal-page__header">
          <a className="back-link" href={pathFor(route.locale)}>
            <span aria-hidden="true">←</span> {copy.backToHome}
          </a>
          <div className="legal-title-row">
            <img
              src={`/assets/branding/${document.app}-app-icon.png`}
              alt=""
              width="88"
              height="88"
            />
            <div>
              <p className="eyebrow">{document.app === "rider" ? copy.riderDocuments : copy.driverDocuments}</p>
              <h1>{document.title}</h1>
            </div>
          </div>
          <p className="legal-summary">{document.summary}</p>
          <p className="updated-date">
            {copy.lastUpdated}: <time dateTime="2026-08-11">{document.updated}</time>
          </p>
        </div>

        <div className="shell legal-layout">
          <aside className="toc">
            <p>{copy.tableOfContents}</p>
            <ol>
              {document.sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.heading.replace(/^\d+\.\s*/, "")}</a>
                </li>
              ))}
            </ol>
          </aside>
          <article className="legal-document">
            {document.sections.map((section) => (
              <LegalSection key={section.id} document={document} sectionId={section.id} />
            ))}
            {document.kind === "privacy" && (
              <p className="official-source">
                {route.locale === "am" ? "የኢትዮጵያ የግል ዳታ ጥበቃ መረጃ፦" : "Official Ethiopian data-protection information:"}{" "}
                <a href="https://pdp.eca.et/pdp-proclamation" rel="noreferrer" target="_blank">
                  pdp.eca.et
                </a>
              </p>
            )}
          </article>
        </div>
      </main>
      <Footer locale={route.locale} copy={copy} />
    </>
  );
}

function LegalSection({ document, sectionId }: { document: LegalDocument; sectionId: string }) {
  const section = document.sections.find((item) => item.id === sectionId);
  if (!section) return null;

  return (
    <section id={section.id}>
      <h2>{section.heading}</h2>
      {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      {section.bullets && (
        <ul>
          {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
        </ul>
      )}
    </section>
  );
}

function NotFoundPage({ route, copy }: { route: Route & { page: "not-found" }; copy: UiCopy }) {
  usePageMetadata(route, `${copy.notFoundTitle} | YegnaTaxi`, copy.notFoundBody);
  return (
    <>
      <Header route={route} copy={copy} />
      <main className="not-found" id="main-content">
        <img src="/assets/branding/yegna-mark.png" alt="" width="140" height="140" />
        <p className="eyebrow">404</p>
        <h1>{copy.notFoundTitle}</h1>
        <p>{copy.notFoundBody}</p>
        <a className="button button--primary" href={pathFor(route.locale)}>{copy.backToHome}</a>
      </main>
      <Footer locale={route.locale} copy={copy} />
    </>
  );
}

function Footer({ locale, copy }: { locale: Locale; copy: UiCopy }) {
  return (
    <footer className="site-footer">
      <div className="shell footer-grid">
        <div>
          <img src="/assets/branding/yegna-wordmark.png" alt="YegnaTaxi" width="148" height="53" loading="lazy" />
          <p>{copy.footerTagline}</p>
          <p className="provider">{copy.providerLine}</p>
        </div>
        <div className="footer-links">
          <div>
            <strong>{copy.riderDocuments}</strong>
            <a href={pathFor(locale, "rider", "privacy")}>{copy.privacyPolicy}</a>
            <a href={pathFor(locale, "rider", "terms")}>{copy.terms}</a>
          </div>
          <div>
            <strong>{copy.driverDocuments}</strong>
            <a href={pathFor(locale, "driver", "privacy")}>{copy.privacyPolicy}</a>
            <a href={pathFor(locale, "driver", "terms")}>{copy.terms}</a>
          </div>
        </div>
      </div>
      <div className="shell footer-bottom">
        <span>© {new Date().getFullYear()} {ORGANIZATION.tradeName}. {copy.rightsReserved}</span>
        <address><strong>{copy.addressLabel}:</strong> {ORGANIZATION.address}</address>
      </div>
    </footer>
  );
}

export default function App() {
  const route = parseRoute(window.location.pathname);
  const copy = UI_COPY[route.locale];

  if (route.page === "home") return <HomePage route={route} copy={copy} />;
  if (route.page === "legal") return <LegalPage route={route} copy={copy} />;
  return <NotFoundPage route={route} copy={copy} />;
}
